import React from "react";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Motion graphics for the navy panel, drawn in code so they stay sharp and follow the step list beside them.
 * Example data only; decorative, so hidden from screen readers.
 */

/** One screen of a mock-up; only the one for the current step is visible */
function Screen({ active, className, children }: { active: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col transition-all duration-500 motion-reduce:transition-none",
        active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A fixed 21x21 pattern that reads as a QR code: three finder squares and scattered modules */
const QR_SIZE = 21;
function qrCells() {
  const cells: Array<[number, number]> = [];
  const inFinder = (x: number, y: number) => (x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12);
  for (let y = 0; y < QR_SIZE; y++) {
    for (let x = 0; x < QR_SIZE; x++) {
      if (inFinder(x, y)) continue;
      if (((x * 7 + y * 13 + x * y * 3) % 5) < 2) cells.push([x, y]);
    }
  }
  return cells;
}
const QR_CELLS = qrCells();

function QrMark({ className }: { className?: string }) {
  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
      <rect width="7" height="7" fill="currentColor" />
      <rect x="1" y="1" width="5" height="5" fill="#fff" />
      <rect x="2" y="2" width="3" height="3" fill="currentColor" />
    </g>
  );
  return (
    <svg viewBox={`0 0 ${QR_SIZE} ${QR_SIZE}`} className={className} shapeRendering="crispEdges" aria-hidden="true">
      <rect width={QR_SIZE} height={QR_SIZE} fill="#fff" />
      {finder(0, 0)}
      {finder(14, 0)}
      {finder(0, 14)}
      {QR_CELLS.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="currentColor" />
      ))}
    </svg>
  );
}

const CODE = ["4", "8", "2", "9", "1", "3"];
const BACKUP_CODES = ["7QK2-M9XP", "R4TD-82LW", "B6HN-3VCA", "Z9FE-K5UJ"];

/**
 * A phone running through the two-step setup: 0 scans the QR code, 1 shows the 6-digit code with its countdown,
 * 2 lists the backup codes with a tick.
 */
export function PhoneVisual({ step }: { step: number }) {
  return (
    <div aria-hidden="true" className="relative mx-auto w-[212px] h-[400px] [@media(max-height:960px)]:[zoom:0.82] [@media(max-height:760px)]:hidden rounded-[34px] bg-[#0B0B3F] border border-white/20 p-2 shadow-2xl shadow-black/40">
      <div className="absolute left-1/2 top-3.5 -translate-x-1/2 h-4 w-20 rounded-full bg-[#0B0B3F] z-10" />
      <div className="relative size-full rounded-[26px] overflow-hidden bg-white text-[#0E1033]">
        {/* 0: camera pointed at the QR code */}
        <Screen active={step === 0} className="bg-[#10122E] items-center justify-center">
          <div className="relative size-[148px]">
            <QrMark className="size-full rounded-lg text-[#10122E]" />
            <span className="absolute -inset-2 rounded-xl border-2 border-white/0" />
            {[
              "top-[-8px] left-[-8px] border-t-2 border-l-2 rounded-tl-lg",
              "top-[-8px] right-[-8px] border-t-2 border-r-2 rounded-tr-lg",
              "bottom-[-8px] left-[-8px] border-b-2 border-l-2 rounded-bl-lg",
              "bottom-[-8px] right-[-8px] border-b-2 border-r-2 rounded-br-lg",
            ].map((c) => (
              <span key={c} className={cn("absolute size-5 border-[#7FA2FF]", c)} />
            ))}
            {step === 0 && <span className="auth-scan absolute left-[-6px] right-[-6px] top-4 h-0.5 rounded-full bg-[#7FA2FF] shadow-[0_0_12px_3px_rgba(127,162,255,0.7)]" />}
          </div>
          <p className="mt-7 text-[11px] text-white/70">Point your camera at the code</p>
        </Screen>

        {/* 1: the authenticator app and its 6-digit code */}
        <Screen active={step === 1} className="px-5 pt-12">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7194]">Authenticator</p>
          <div className="mt-4 rounded-2xl border border-[#E2E5F0] p-4">
            <div className="flex items-center gap-2.5">
              <span className="size-7 rounded-lg bg-[#000080] text-white text-[11px] font-bold flex items-center justify-center">N</span>
              <div className="leading-tight">
                <p className="text-[12px] font-semibold">Nelgates Sales</p>
                <p className="text-[10px] text-[#6B7194]">priya@company.com</p>
              </div>
            </div>
            <div className="mt-5 flex justify-center gap-1" key={step === 1 ? "on" : "off"}>
              {CODE.map((d, i) => (
                <span
                  key={i}
                  className={cn("text-[26px] font-semibold tabular-nums text-[#000080]", step === 1 && "auth-pop", i === 2 && "mr-2")}
                  style={{ animationDelay: `${0.15 * i}s` }}
                >
                  {d}
                </span>
              ))}
            </div>
            <div className="mt-4 h-1 rounded-full bg-[#E9EDFA] overflow-hidden">
              {step === 1 && <span className="auth-progress block h-full w-full rounded-full bg-[#000080]" style={{ animationDuration: "2.4s", animationIterationCount: "infinite" }} />}
            </div>
            <p className="mt-1.5 text-[10px] text-[#6B7194] text-center">Changes every 30 seconds</p>
          </div>
        </Screen>

        {/* 2: backup codes saved */}
        <Screen active={step === 2} className="px-5 pt-12">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7194]">Backup codes</p>
          <div className="mt-4 rounded-2xl border border-[#E2E5F0] p-4 space-y-2.5">
            {BACKUP_CODES.map((c, i) => (
              <p
                key={c}
                className={cn("font-mono text-[13px] tracking-wide rounded-md bg-[#F3F5FB] px-2.5 py-1.5", step === 2 && "auth-fade")}
                style={{ animationDelay: `${0.12 * i}s` }}
              >
                {c}
              </p>
            ))}
          </div>
          <p className={cn("mt-4 mx-auto inline-flex items-center gap-1.5 rounded-full bg-[#E3F4EA] px-3 py-1.5 text-[11px] font-medium text-[#1F7A4D]", step === 2 && "auth-pop")} style={{ animationDelay: "0.6s" }}>
            <Check className="size-3.5" /> Saved
          </p>
        </Screen>
      </div>
    </div>
  );
}

/**
 * The password recovery journey: 0 the reset email arrives, 1 the link is opened, 2 a new password is chosen.
 */
export function MailVisual({ step }: { step: number }) {
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-[380px] h-[250px] rounded-2xl bg-white text-[#0E1033] shadow-2xl shadow-black/30 overflow-hidden">
      {/* 0: the email lands */}
      <Screen active={step === 0} className="p-5">
        <div className="flex items-center gap-2.5">
          <span className="size-8 rounded-lg bg-[#000080] text-white text-xs font-bold flex items-center justify-center">N</span>
          <div className="leading-tight">
            <p className="text-[13px] font-semibold">Nelgates Sales</p>
            <p className="text-[11px] text-[#6B7194]">to you · just now</p>
          </div>
        </div>
        <p className="mt-4 text-[15px] font-semibold">Reset your password</p>
        <p className="mt-1 text-[12px] text-[#6B7194] leading-relaxed">Use the button below to choose a new password. The link works once.</p>
        <span className={cn("mt-4 self-start rounded-lg bg-[#000080] px-4 py-2 text-[12px] font-medium text-white", step === 0 && "auth-pulse")}>Choose a new password</span>
        <p className="mt-auto text-[11px] text-[#6B7194]">Link expires in 30 minutes</p>
      </Screen>

      {/* 1: the link opens */}
      <Screen active={step === 1} className="items-center justify-center gap-3">
        <span className={cn("size-12 rounded-full bg-[#E9EDFA] text-[#000080] flex items-center justify-center", step === 1 && "auth-pop")}>
          <Lock className="size-5" />
        </span>
        <p className="text-[14px] font-semibold">Link verified</p>
        <div className="h-1 w-40 rounded-full bg-[#E9EDFA] overflow-hidden">
          {step === 1 && <span className="auth-progress block h-full w-full bg-[#000080]" style={{ animationDuration: "2s" }} />}
        </div>
        <p className="text-[11px] text-[#6B7194]">Opening the password page</p>
      </Screen>

      {/* 2: a new password */}
      <Screen active={step === 2} className="p-5">
        <p className="text-[15px] font-semibold">Choose a new password</p>
        <div className="mt-4 h-10 rounded-lg border border-[#C9CEE3] px-3 flex items-center text-[13px] tracking-[0.3em] text-[#0E1033]">••••••••••••</div>
        <div className="mt-3 grid grid-cols-4 gap-1">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={cn("h-1 rounded-full bg-[#1F7A4D] origin-left", step === 2 && "auth-progress")} style={{ animationDelay: `${0.25 * i}s`, animationDuration: "0.4s" }} />
          ))}
        </div>
        <p className="mt-1.5 text-[11px] font-medium text-[#1F7A4D]">Strong</p>
        <span className={cn("mt-auto self-end inline-flex items-center gap-1.5 rounded-lg bg-[#000080] px-4 py-2 text-[12px] font-medium text-white", step === 2 && "auth-pop")} style={{ animationDelay: "1s" }}>
          <Check className="size-3.5" /> Password saved
        </span>
      </Screen>
    </div>
  );
}
